import React, { useEffect, useMemo, useState } from 'react';
import { OptionList, OptionItem } from '@components/common/dropdown';
import ChevronDownBold from '@assets/svgs/ChevronDownBold.svg';
import WarningOvalSolid from '@assets/svgs/WarningOvalSolid.svg';
import type { OptionItem as OptionItemType } from '@steadioTypes/dropdown';

type SelectProps = {
  title: string;
  name: string;
  placeholder: string;
  value: string;
  options: OptionItemType[];
  error: boolean;
  hasGuide: boolean;
  guideMessage: React.ReactNode;
  SVGIcon: any;
  disabled: boolean;
  completed: boolean;
  onChange: (value: string) => void;
  onClose?: (value: string) => void;
};

export default function Select({
  title,
  name,
  placeholder,
  value,
  options,
  error,
  hasGuide,
  guideMessage,
  SVGIcon,
  disabled,
  completed,
  onChange,
  onClose,
}: SelectProps) {
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const isSelectable = !completed && !disabled;

  useEffect(() => {
    if (!isSelectable) {
      setIsOpen(false);
    }
  }, [isSelectable]);

  const valueDescription = useMemo(
    () =>
      options.filter((option) => option.value === value)[0]?.description || '',
    [options, value],
  );

  const openOptions = () => {
    if (!isSelectable) {
      return;
    }
    setIsOpen(true);
  };

  const closeOptions = (event: React.FocusEvent<HTMLInputElement>) => {
    setIsOpen(false);
    const optionValue = event.relatedTarget?.getAttribute('data-option-value');
    if (optionValue !== null && optionValue !== undefined) {
      onChange(optionValue);
    }
    onClose && onClose(optionValue || value);
  };

  const hasOptions = options.length > 0;

  return (
    <div>
      {title && (
        <strong className={`block font_label_bold_md content_primary mb-[8px]`}>
          {title}
        </strong>
      )}
      <label className={labelStyle(error, isSelectable)}>
        {SVGIcon && (
          <SVGIcon
            className={`h-[24px] w-[24px] mr-[10px] ${
              !isSelectable && 'content_disabled'
            }`}
          />
        )}
        <input
          type="text"
          name={name}
          readOnly
          disabled={!isSelectable}
          value={valueDescription as string}
          placeholder={placeholder}
          className={`${inputStyle(disabled, completed)}`}
          onFocus={openOptions}
          onBlur={closeOptions}
        />
        <ChevronDownBold
          className={`ml-[10px] h-[16px] w-[16px] ${
            isSelectable ? 'content_secondary' : 'content_disabled'
          }`}
        />
      </label>
      <div className="relative flex">
        {hasGuide && (
          <span
            className={`flex mt-[8px] h-[16px] font_label_regular_sm ${
              error ? 'content_negative' : 'content_quaternary'
            }`}
          >
            {error && (
              <WarningOvalSolid className="h-[16px] w-[16px] mr-[4px]" />
            )}
            {guideMessage}
          </span>
        )}
        {isOpen && hasOptions && (
          <div className={optionListStyle}>
            <OptionList>
              {options.map((optionItem) => (
                <OptionItem key={optionItem.value} optionItem={optionItem} />
              ))}
            </OptionList>
          </div>
        )}
      </div>
    </div>
  );
}

Select.defaultProps = {
  title: '',
  name: '',
  placeholder: '',
  value: '',
  options: [],
  error: false,
  hasGuide: false,
  guideMessage: '',
  disabled: false,
  completed: false,
  SVGIcon: null,
};

const labelStyle = (error: boolean, isSelectable: boolean) => {
  const defaultClasses = [
    'rounded-[8px]',
    'h-[48px]',
    'flex',
    'items-center',
    isSelectable ? 'cursor-pointer' : 'cursor-default',
  ];

  const errorClasses = [
    'border-2',
    'border_negative_active',
    'pl-[19px]',
    'pr-[15px]',
  ];

  const notFocusedClasses = [
    'border',
    'border_black_opacity',
    'pl-[20px]',
    'pr-[16px]',
  ];

  const focusedClasses = [
    'focus-within:border-2',
    'focus-within:border_accent_active',
    'focus-within:pl-[19px]',
    'focus-within:pr-[15px]',
  ];

  return [
    isSelectable ? 'surface_primary' : 'surface_disabled',
    ...defaultClasses,
    ...(error ? errorClasses : [...notFocusedClasses, ...focusedClasses]),
  ].join(' ');
};

const inputStyle = (disabled: boolean, completed: boolean) => {
  const isSelectable = !completed && !disabled;

  return [
    'outline-none',
    'w-full',
    'font_body_regular_md',
    'placeholder:content_disabled',
    isSelectable ? 'cursor-pointer' : 'cursor-default',
    isSelectable ? 'surface_primary' : 'surface_disabled',
    disabled ? 'content_disabled' : 'content_primary',
  ].join(' ');
};

const optionListStyle = [
  'absolute',
  'top-[8px]',
  'z-10',
  'max-h-[192px]',
  'overflow-y-auto',
  'w-full',
  'shadow-menu',
].join(' ');
